import { useEffect, useState } from 'react';
import locations from '@/data/locations.json';

/* ============================
   Types
============================ */

export type NearestLocation = {
  country: string;
  state: string;
  city: string;
};

export type City = {
  key: string;
  country: string;
  state: string;
  city: string;
  lat: number;
  lng: number;
};

type IpGeo = {
  ip?: string;
  latitude: number;
  longitude: number;
};

/* ============================
   Cities from locations.json
============================ */

export const CITIES: City[] = locations.map((l) => ({
  key: `${l.Country}_${l.State}_${l.City}`.replace(/\s+/g, ''),
  country: l.Country,
  state: l.State,
  city: l.City,
  lat: l.lat,
  lng: l.lng,
}));

/* ============================
   IP Location
============================ */

export async function getIpLocation(): Promise<IpGeo | null> {
  try {
    const res = await fetch('https://ipapi.co/json/');
    const data = await res.json();

    if (!data.latitude || !data.longitude) return null;

    return {
      ip: data.ip,
      latitude: data.latitude,
      longitude: data.longitude,
    };
  } catch {
    return null;
  }
}

/* ============================
   Distance (Haversine)
============================ */

function toRad(v: number) {
  return (v * Math.PI) / 180;
}

function distanceKm(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
) {
  const R = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);

  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLng / 2) ** 2;

  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/* ============================
   Nearest City
============================ */

export function findNearestCity(
  lat: number,
  lng: number,
  cities: City[]
): City | null {
  let nearest: City | null = null;
  let minDistance = Infinity;

  for (const city of cities) {
    const d = distanceKm(lat, lng, city.lat, city.lng);
    if (d < minDistance) {
      minDistance = d;
      nearest = city;
    }
  }

  return nearest;
}

/* ============================
   React Hook
============================ */

const STORAGE_KEY = 'nearestCity';

export function useNearestCity() {
  const [location, setLocation] = useState<NearestLocation | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // 1️⃣ Cached result
    const cachedKey = localStorage.getItem(STORAGE_KEY);
    if (cachedKey) {
      const cachedCity = CITIES.find(c => c.key === cachedKey);
      if (cachedCity) {
        setLocation({
          country: cachedCity.country,
          state: cachedCity.state,
          city: cachedCity.city,
        });
        setLoading(false);
        return;
      }
    }

    // 2️⃣ IP lookup + distance match
    async function resolveCity() {
      const loc = await getIpLocation();

      if (!loc) {
        setLoading(false);
        return;
      }

      const nearest = findNearestCity(
        loc.latitude,
        loc.longitude,
        CITIES
      );

      if (nearest) {
        setLocation({
          country: nearest.country,
          state: nearest.state,
          city: nearest.city,
        });
        localStorage.setItem(STORAGE_KEY, nearest.key);
      }

      setLoading(false);
    }

    resolveCity();
  }, []);

  return { location, loading };
}
